import {inject, Injectable} from '@angular/core';
import {DEX_NONE_STR_VALUE} from '../const';
import {DexSecuritiesSubjectService} from '../subjects/dex-securities-subject-service';
import {ComputedHeldPosition} from '../types/computed/computed-held-position';
import {
    DelClassificationBaseValueDeliverable,
    DelClassificationPerpetualFuture,
    DeliverableClassification
} from '../types/del-classifcation';
import {Deliverable} from '../types/deliverable';
import {HeldPosition} from '../types/held-position';
import {ValueWithDel, vToDec} from '../utils';

// TODO: debug
const dexLogs = {
    yieldCalc: [] as any
};
(window as any).dexLogs = dexLogs;

const BASIS_POINTS_DECIMAL_PLACES = 6;

@Injectable()
export class DexComputedHeldPositionService {

    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);

    public create(pos: HeldPosition): ComputedHeldPosition {
        const deliverable = this.dexSecuritiesSubjectService.getDeliverableById(pos.identifier.deliverableId);
        const valueDeliverable = this.dexSecuritiesSubjectService.getDeliverableById(pos.valueDeliverableId);

        const getDelFromClass = <T_CLASS extends DeliverableClassification>(
            d: Deliverable,
            k1: keyof T_CLASS,
            k2: keyof T_CLASS[typeof k1]
        ): Deliverable => {
            const idStr = d.classification[k1]?.[k2] as string;

            return idStr
                ? this.dexSecuritiesSubjectService.getDeliverableById(Number(idStr)) || d
                : d;
        };

        const settlementDel = deliverable && getDelFromClass<DelClassificationPerpetualFuture>(
            deliverable,
            'perpetual_future',
            'settlement_deliverable_id'
        );

        const baseDel = valueDeliverable && getDelFromClass<DelClassificationBaseValueDeliverable>(
            valueDeliverable,
            'base_value_deliverable',
            'base_value_deliverable_id'
        );

        const strToBigInt = (v: string): bigint | undefined =>
            v && v !== DEX_NONE_STR_VALUE ? BigInt(v) : undefined;

        const strToNumber = (v: string): number =>
            v && v !== DEX_NONE_STR_VALUE ? Number(v) : undefined;

        return {
            raw: pos,
            deliverable,
            valueDeliverable,
            ...this.calcYieldAndUPnl(pos, valueDeliverable),
            balance:        {v: strToBigInt(pos.balance), d: deliverable},
            entryValue:     {v: strToBigInt(pos.entryValue), d: settlementDel},
            referenceRate:  {v: strToBigInt(pos.referenceRate), d: baseDel},
            currentValue:   {v: strToBigInt(pos.currentValue), d: valueDeliverable},
            profitOrLoss:   {v: strToBigInt(pos.profitOrLoss), d: valueDeliverable},
            portfolioValue: {v: strToBigInt(pos.portfolioValue), d: valueDeliverable},
            yield:          {v: strToBigInt(pos.yield), dp: BASIS_POINTS_DECIMAL_PLACES},
            yieldNumber:    strToNumber(pos.yield)
        };
    }

    private calcYieldAndUPnl(pos: HeldPosition, valueDeliverable: Deliverable): {
        uPnl: bigint | undefined,
        yieldPercent: number | undefined
    } {
        const result: {
            uPnl: bigint,
            yieldPercent: number
        } = {
            uPnl:         undefined,
            yieldPercent: pos.yield && pos.yield !== DEX_NONE_STR_VALUE
                              ? vToDec(pos.yield, BASIS_POINTS_DECIMAL_PLACES).toNumber()
                              : undefined
        };

        const entry = BigInt(pos.entryValue);

        if (entry) {
            const uPnlV: ValueWithDel = {
                v: BigInt(pos.profitOrLoss),
                d: valueDeliverable
            };

            result.uPnl = uPnlV.v;
        }

        return result;
    }

}
